/**
 * Reordering within a single FAQ category (DAB-858).
 *
 * Ported from Testimonials' `testimonialOrder.js`, with one addition: FAQ
 * rows routinely carry missing or tied `order` — WP-imported questions have
 * none at all (see my-pet-api/src/scripts/importWpFaq.ts), and the
 * pre-migration collection has outright ties. Swapping two tied rows would
 * patch both to the same number and visibly change nothing, so such a
 * category is renumbered as a whole before the swap.
 *
 * A move only ever reorders questions inside the same category: `rows` must
 * already be filtered to the category the moved item belongs to.
 */

const hasDistinctOrders = (rows) => {
  const orders = rows.map((row) => row.order);
  return (
    orders.every((order) => Number.isFinite(order)) &&
    new Set(orders).size === orders.length
  );
};

/**
 * `rows`, sorted by descending order (highest first, matching the API's own
 * sort) and renumbered to dense, distinct values counting down from the
 * list's length. `Array.prototype.sort` is stable since ES2019, so two rows
 * that tie (or both lack an order) keep the relative position the list
 * already had — i.e. whatever order the API returned them in.
 */
export const normalizeCategoryOrders = (rows) => {
  const list = rows || [];
  const sorted = [...list].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
  return sorted.map((row, index) => ({ ...row, order: sorted.length - index }));
};

/**
 * The `{ id, order }` patches that swap one FAQ with its neighbour within its
 * category. An impossible move (top row up, bottom row down, unknown id)
 * returns [].
 *
 * Distinct stored orders: the pair exchanges its own values — two writes.
 * Ties or missing orders: the whole category is renumbered and every row
 * whose stored order changes is patched. Patching only the pair would leave
 * the others on their old, tied values, and the API's `_id` tiebreak would
 * then show the pair somewhere other than where the editor moved it.
 */
export const swapPatches = (rows, id, direction) => {
  const source = rows || [];
  const list = hasDistinctOrders(source)
    ? [...source].sort((a, b) => b.order - a.order)
    : normalizeCategoryOrders(source);

  const i = list.findIndex((row) => row._id === id);
  const j = direction === "up" ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= list.length) return [];

  const target = list.map((row) => ({ id: row._id, order: row.order }));
  target[i] = { id: list[i]._id, order: list[j].order };
  target[j] = { id: list[j]._id, order: list[i].order };

  const stored = new Map(source.map((row) => [row._id, row.order]));
  return target.filter((patch) => stored.get(patch.id) !== patch.order);
};
